import type { ReportQueryResponse } from '@/api/types';

export const PERFORMANCE_METRIC_IDS = [
  'gross_spend',
  'impressions',
  'clicks',
  'ctr',
  'cpm',
  'cpc',
  'attributed_conversions',
  'attributed_revenue',
  'cpa',
  'roas',
] as const;

export type PerformanceMetricId = typeof PERFORMANCE_METRIC_IDS[number];

export const PERFORMANCE_SORT_FIELDS = [
  ...PERFORMANCE_METRIC_IDS,
] as const;

export type PerformanceSortField = typeof PERFORMANCE_SORT_FIELDS[number];
export type PerformanceSortDirection = 'asc' | 'desc';

/**
 * The only metric shape the show-facing Performance UI may consume.
 *
 * Provider attribution stays in micros until the final formatting boundary so
 * modeled fractional conversions and sub-cent corrections are never rounded
 * once per row and then summed again.
 */
export interface DirectPerformanceMetrics {
  gross_spend_cents: number;
  /** Gross-spend denominator aligned to the attribution publication clock. */
  attribution_gross_spend_cents: number | null;
  impressions: number;
  clicks: number;
  attributed_conversions_micros: number;
  attributed_revenue_micros: number;
}

type ReportMetricSet = ReportQueryResponse['totals'];

function finite(value: unknown): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function finiteOptional(value: unknown): number | null {
  const number = Number(value);
  return value != null && Number.isFinite(number) ? number : null;
}

export function directPerformanceMetrics(
  metrics: ReportMetricSet,
): DirectPerformanceMetrics {
  return {
    gross_spend_cents: finite(metrics.billable_spend_cents),
    attribution_gross_spend_cents: finiteOptional(
      metrics.attribution_billable_spend_cents,
    ),
    impressions: finite(metrics.impressions),
    clicks: finite(metrics.clicks),
    attributed_conversions_micros: finite(metrics.assigned_conversions_micros),
    attributed_revenue_micros: finite(metrics.assigned_revenue_micros),
  };
}

export function sumDirectPerformanceMetrics(
  rows: readonly DirectPerformanceMetrics[],
): DirectPerformanceMetrics {
  const total = rows.reduce<DirectPerformanceMetrics>((sum, row) => ({
    gross_spend_cents: sum.gross_spend_cents + row.gross_spend_cents,
    attribution_gross_spend_cents: null,
    impressions: sum.impressions + row.impressions,
    clicks: sum.clicks + row.clicks,
    attributed_conversions_micros:
      sum.attributed_conversions_micros + row.attributed_conversions_micros,
    attributed_revenue_micros:
      sum.attributed_revenue_micros + row.attributed_revenue_micros,
  }), emptyDirectPerformanceMetrics());
  total.attribution_gross_spend_cents = rows.every(
    (row) => row.attribution_gross_spend_cents !== null,
  )
    ? rows.reduce((sum, row) => sum + row.attribution_gross_spend_cents!, 0)
    : null;
  return total;
}

export function emptyDirectPerformanceMetrics(): DirectPerformanceMetrics {
  return {
    gross_spend_cents: 0,
    attribution_gross_spend_cents: null,
    impressions: 0,
    clicks: 0,
    attributed_conversions_micros: 0,
    attributed_revenue_micros: 0,
  };
}

export function attributedConversions(metrics: DirectPerformanceMetrics): number {
  return metrics.attributed_conversions_micros / 1_000_000;
}

export function attributedRevenueDollars(metrics: DirectPerformanceMetrics): number {
  return metrics.attributed_revenue_micros / 1_000_000;
}

export function grossSpendDollars(metrics: DirectPerformanceMetrics): number {
  return metrics.gross_spend_cents / 100;
}

export function attributionGrossSpendDollars(
  metrics: DirectPerformanceMetrics,
): number | null {
  return metrics.attribution_gross_spend_cents == null
    ? null
    : metrics.attribution_gross_spend_cents / 100;
}

export function performanceMetricValue(
  metrics: DirectPerformanceMetrics,
  metric: PerformanceMetricId,
): number | null {
  const spendDollars = grossSpendDollars(metrics);
  const attributionSpendDollars = attributionGrossSpendDollars(metrics);
  const conversions = attributedConversions(metrics);
  switch (metric) {
    case 'gross_spend':
      return spendDollars;
    case 'impressions':
      return metrics.impressions;
    case 'clicks':
      return metrics.clicks;
    case 'ctr':
      return metrics.impressions > 0
        ? metrics.clicks / metrics.impressions * 100
        : null;
    case 'cpm':
      return metrics.impressions > 0
        ? spendDollars * 1_000 / metrics.impressions
        : null;
    case 'cpc':
      return metrics.clicks > 0 ? spendDollars / metrics.clicks : null;
    case 'attributed_conversions':
      return conversions;
    case 'attributed_revenue':
      return attributedRevenueDollars(metrics);
    case 'cpa':
      return conversions > 0 && attributionSpendDollars !== null
        ? attributionSpendDollars / conversions
        : null;
    case 'roas':
      return attributionSpendDollars !== null && attributionSpendDollars > 0
        ? attributedRevenueDollars(metrics) / attributionSpendDollars
        : null;
  }
}

export function comparePerformanceMetrics(
  left: DirectPerformanceMetrics,
  right: DirectPerformanceMetrics,
  field: PerformanceSortField,
  direction: PerformanceSortDirection,
): number {
  const leftValue = performanceMetricValue(left, field);
  const rightValue = performanceMetricValue(right, field);
  // Undefined ratios are always last; changing direction must not promote a
  // divide-by-zero row above measured performance.
  if (leftValue === null) return rightValue === null ? 0 : 1;
  if (rightValue === null) return -1;
  const order = leftValue - rightValue;
  return direction === 'asc' ? order : -order;
}

export function hasDirectDeliveryEvidence(metrics: DirectPerformanceMetrics): boolean {
  return metrics.gross_spend_cents !== 0
    || metrics.impressions !== 0
    || metrics.clicks !== 0;
}

export function hasDirectAttributionEvidence(metrics: DirectPerformanceMetrics): boolean {
  return metrics.attributed_conversions_micros !== 0
    || metrics.attributed_revenue_micros !== 0;
}
